import '../Header/Header.scss';
import './AppShell.scss';

import {
  mdiAccountGroupOutline,
  mdiAccountMultipleOutline,
  mdiBellOutline,
  mdiCalendarMonthOutline,
  mdiChartBar,
  mdiClipboardTextOutline,
  mdiCogOutline,
  mdiDomain,
  mdiFileDocumentOutline,
  mdiFlagOutline,
  mdiFolderOutline,
  mdiHelpCircleOutline,
  mdiHistory,
  mdiInboxOutline,
  mdiLockOpenVariantOutline,
  mdiLockOutline,
  mdiMapMarkerOutline,
  mdiMenu,
  mdiPackageVariantClosed,
  mdiWrenchOutline
} from '@mdi/js';
import { ConfigProvider, Drawer, Grid, Layout, Menu, Tooltip } from 'antd';
import { MAIN_IMAGE_ROOT } from 'app_config';
import colors from 'common/colors.module.scss';
import type { AppUser } from 'common/models';
import { useGetCompanyAndUserInfoQuery } from 'common/queries';
import useAuth from 'common/useAuth';
import Button from 'components/Button/Button';
import { useAdminNavigationItems } from 'components/Header/AdminHeader';
import Header from 'components/Header/Header';
import { useUserNavigationItems } from 'components/Header/UserHeader';
import UserMenu from 'components/Header/UserMenu';
import UserNotifications from 'components/Header/UserNotifications';
import Icon from 'components/Icon/Icon';
import ImageComponent from 'components/ImageComponent/ImageComponent';
import { PageToolbarProvider } from 'components/Layout/PageToolbar';
import NavLink, { isActiveLink } from 'components/NavLink/NavLink';
import { useCustomTranslation } from 'helpers/translationHelper';
import type { PropsWithChildren, ReactNode } from 'react';
import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import logo from 'static/images/logo_light.png';

import { useNavigationLayout } from './NavigationLayoutProvider';
import RecentHistoryMenu from './RecentHistoryMenu';

interface NavigationItem {
  to: string;
  label: ReactNode;
  exact?: boolean;
  hidden?: boolean;
  activeRoutes?: string[];
  inactiveRoutes?: string[];
}

const icons: Record<string, string> = {
  '/': mdiCalendarMonthOutline,
  '/orders': mdiFolderOutline,
  '/interventions': mdiMapMarkerOutline,
  '/customForms': mdiFileDocumentOutline,
  '/request': mdiInboxOutline,
  '/customers': mdiAccountMultipleOutline,
  '/serviceObjects': mdiWrenchOutline,
  '/material': mdiPackageVariantClosed,
  '/dashboard': mdiChartBar,
  '/settings': mdiCogOutline,
  '/admin/companies': mdiDomain,
  '/admin/leads': mdiAccountGroupOutline,
  '/admin/logs': mdiHistory,
  '/admin/templates': mdiFileDocumentOutline,
  '/admin/featureflags': mdiFlagOutline,
  '/admin/announcements': mdiBellOutline,
  '/admin/supportTickets': mdiHelpCircleOutline,
  '/admin/users': mdiAccountGroupOutline,
  '/admin/customerUsers': mdiAccountMultipleOutline,
  '/admin/subscriptions': mdiClipboardTextOutline
};

interface NavigationItemsProps {
  items: NavigationItem[];
  expanded: boolean;
  onNavigate: () => void;
}

const NavigationItems = ({ items, expanded, onNavigate }: NavigationItemsProps) => {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const visibleItems = items.filter((item) => !item.hidden);
  const activeItem = visibleItems.find((item) =>
    isActiveLink(pathname, item.to, item.activeRoutes, item.inactiveRoutes, item.exact)
  );

  return (
    <Menu
      className="AppNavigation_menu"
      mode="inline"
      theme="dark"
      inlineCollapsed={!expanded}
      selectedKeys={activeItem ? [activeItem.to] : []}
      onClick={({ key, domEvent }) => {
        // Pointer navigation uses the real link (including modified/new-tab clicks).
        // Menu's keyboard activation does not click that link automatically.
        if ('key' in domEvent) navigate(key);
        onNavigate();
      }}
      items={visibleItems.map((item) => ({
        key: item.to,
        title: !expanded && typeof item.label === 'string' ? item.label : '',
        icon: <Icon path={icons[item.to] || mdiFolderOutline} />,
        label: (
          <NavLink
            to={item.to}
            label={item.label}
            exact={item.exact}
            activeRoutes={item.activeRoutes}
            inactiveRoutes={item.inactiveRoutes}
          />
        )
      }))}
    />
  );
};

const UserNavigation = ({
  user,
  expanded,
  onNavigate,
  hideStores,
  restrictTechnicianDataVisibility
}: Omit<NavigationItemsProps, 'items'> & {
  user: AppUser;
  hideStores?: boolean;
  restrictTechnicianDataVisibility?: boolean;
}) => {
  const items = useUserNavigationItems({ user, hideStores, restrictTechnicianDataVisibility });
  return <NavigationItems items={items} expanded={expanded} onNavigate={onNavigate} />;
};

const AdminNavigation = (props: Omit<NavigationItemsProps, 'items'>) => {
  const items = useAdminNavigationItems();
  return <NavigationItems {...props} items={items} />;
};

const ShellHeader = ({
  mobile,
  mobileOpen,
  onOpen,
  logoUrl,
  companyName
}: {
  mobile: boolean;
  mobileOpen: boolean;
  onOpen: () => void;
  logoUrl: string;
  companyName?: string;
}) => {
  const { layout } = useNavigationLayout();
  const { tCommon } = useCustomTranslation();
  if (layout === 'top') return <Header />;
  if (layout === 'combined')
    return (
      <Header
        variant="utilities"
        mobileNavigation={mobile ? { open: mobileOpen, onOpen } : undefined}
      />
    );
  if (!mobile) return null;
  return (
    <header className="AppShell_mobileHeader">
      <Button
        type="text"
        onClick={onOpen}
        aria-label={tCommon('navigation_open', { defaultValue: 'Otevřít navigaci' })}
        aria-expanded={mobileOpen}
        aria-controls="app-navigation"
      >
        <Icon path={mdiMenu} />
      </Button>
      <Link to="/" aria-label={tCommon('navigation_home', { defaultValue: 'Úvodní stránka' })}>
        <ImageComponent url={logoUrl} alt={companyName || 'mWork365'} />
      </Link>
    </header>
  );
};

const getToolbarPlacement = (combined: boolean, isSuperAdmin = false) =>
  combined && isSuperAdmin ? 'header' : 'content';

const AppShell = ({ children }: PropsWithChildren) => {
  const { layout } = useNavigationLayout();
  const topNavigation = layout === 'top';
  const combinedNavigation = layout === 'combined';
  const { tCommon } = useCustomTranslation();
  const { loading } = useAuth();
  const { data } = useGetCompanyAndUserInfoQuery();
  const user = data?.user;
  const company = data?.company;
  const { pathname } = useLocation();
  const screens = Grid.useBreakpoint();
  const mobile = screens.md === false;
  const { mode, setMode } = useNavigationLayout();
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const expanded =
    mobile ||
    mode === 'open' ||
    (mode === 'auto' && (hovered || focused || userMenuOpen || notificationsOpen || historyOpen));
  const logoUrl = company?.headerLogoUrl ? `${MAIN_IMAGE_ROOT}${company.headerLogoUrl}` : logo;

  useEffect(() => {
    setMobileOpen(false);
  }, [pathname]);

  useEffect(() => {
    setHovered(false);
    setFocused(false);
    setUserMenuOpen(false);
    setNotificationsOpen(false);
    setHistoryOpen(false);
    setMobileOpen(false);
  }, [mobile]);

  const changeMode = () => {
    const nextMode = mode === 'auto' ? 'open' : mode === 'open' ? 'closed' : 'auto';
    setMode(nextMode);
  };

  const modeLabel = tCommon(`navigation_mode_${mode}`, {
    defaultValue: {
      auto: 'Automatické rozbalení',
      open: 'Navigace připnutá',
      closed: 'Navigace sbalená'
    }[mode]
  });
  const nextModeLabel = tCommon(`navigation_next_mode_${mode}`, {
    defaultValue: {
      auto: 'Připnout navigaci',
      open: 'Trvale sbalit navigaci',
      closed: 'Zapnout automatické rozbalení'
    }[mode]
  });
  const navigation = (
    <nav
      id="app-navigation"
      aria-label={tCommon('navigation_main', { defaultValue: 'Hlavní navigace' })}
      className={`AppNavigation ${expanded ? 'is-expanded' : 'is-collapsed'}`}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onPointerDownCapture={() => setFocused(false)}
      onKeyDownCapture={() => setFocused(true)}
      onFocus={(event) => setFocused(event.target.matches(':focus-visible'))}
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) setFocused(false);
      }}
    >
      {!mobile && (
        <Link
          to="/"
          className={`AppNavigation_logo ${company?.headerLogoUrl ? 'is-custom' : ''}`}
          aria-label={tCommon('navigation_home', { defaultValue: 'Úvodní stránka' })}
          onClick={() => setMobileOpen(false)}
        >
          <ImageComponent url={logoUrl} alt={company?.name || 'mWork365'} />
        </Link>
      )}

      <div className="AppNavigation_items">
        {!loading &&
          user &&
          (user.isSuperAdmin ? (
            <AdminNavigation expanded={expanded} onNavigate={() => setMobileOpen(false)} />
          ) : (
            <UserNavigation
              user={user}
              expanded={expanded}
              onNavigate={() => setMobileOpen(false)}
              hideStores={company?.hideStores}
              restrictTechnicianDataVisibility={company?.restrictTechnicianDataVisibility}
            />
          ))}
      </div>

      {(!combinedNavigation || !mobile) && (
        <div className="AppNavigation_footer">
          {!mobile && (
            <Tooltip title={`${modeLabel} · ${nextModeLabel}`} placement="right">
              <Button
                type="text"
                className={`AppNavigation_pin${mode === 'closed' ? ' is-closed' : ''}`}
                onClick={changeMode}
                aria-label={`${modeLabel}. ${nextModeLabel}`}
                aria-expanded={expanded}
              >
                <Icon path={mode === 'auto' ? mdiLockOpenVariantOutline : mdiLockOutline} />
                {expanded && <span>{modeLabel}</span>}
              </Button>
            </Tooltip>
          )}
          {!combinedNavigation && (
            <>
              <RecentHistoryMenu
                expanded={expanded}
                mobile={mobile}
                onOpenChange={setHistoryOpen}
                onNavigate={() => setMobileOpen(false)}
              />
              {user && !user.isSuperAdmin && (
                <div className="AppNavigation_notifications">
                  <UserNotifications
                    placement={mobile ? 'topLeft' : 'rightBottom'}
                    onOpenChange={setNotificationsOpen}
                    label={
                      expanded
                        ? tCommon('navigation_notifications', { defaultValue: 'Notifikace' })
                        : undefined
                    }
                  />
                </div>
              )}
              <UserMenu
                compact={!expanded}
                placement={mobile ? 'topLeft' : 'rightBottom'}
                onOpenChange={setUserMenuOpen}
              />
            </>
          )}
        </div>
      )}
    </nav>
  );

  return (
    <PageToolbarProvider placement={getToolbarPlacement(combinedNavigation, user?.isSuperAdmin)}>
      <ConfigProvider
        theme={{
          components: {
            Menu: {
              darkItemBg: colors.blue,
              darkSubMenuItemBg: colors.blue,
              darkItemColor: colors.white,
              darkItemSelectedBg: colors.blue20,
              darkItemSelectedColor: colors.orange,
              darkItemHoverColor: colors.orange,
              itemBorderRadius: 0,
              itemMarginInline: 0,
              itemHeight: 44
            }
          }
        }}
      >
        <Layout className="AppShell">
          {topNavigation ? null : mobile ? (
            <Drawer
              title={tCommon('navigation_main', { defaultValue: 'Hlavní navigace' })}
              placement="left"
              open={mobileOpen}
              onClose={() => setMobileOpen(false)}
              forceRender
              size={280}
              classNames={{ root: 'AppNavigation_drawer' }}
            >
              {navigation}
            </Drawer>
          ) : (
            <Layout.Sider
              className="AppShell_rail"
              width={240}
              collapsedWidth={64}
              collapsed={mode !== 'open'}
              trigger={null}
            >
              {navigation}
            </Layout.Sider>
          )}
          <Layout.Content className="AppShell_content">
            <ShellHeader
              mobile={mobile}
              mobileOpen={mobileOpen}
              onOpen={() => setMobileOpen(true)}
              logoUrl={logoUrl}
              companyName={company?.name}
            />
            {children}
          </Layout.Content>
        </Layout>
      </ConfigProvider>
    </PageToolbarProvider>
  );
};

export default AppShell;
